import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import { featuredDoctors } from '../data/doctors';
import Seo from '../components/Seo';

export default function DoctorsPage() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedDoctorId, setExpandedDoctorId] = useState(null);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const pointerStartRef = useRef(null);
  useScrollReveal();

  useEffect(() => {
    setDoctors(featuredDoctors);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!doctors.length) return;
    setActiveIndex((current) => Math.min(current, doctors.length - 1));
  }, [doctors]);

  useEffect(() => {
    setShowSwipeHint(true);
  }, []);

  useEffect(() => {
    if (loading || !doctors.length || hasUserInteracted || expandedDoctorId) return undefined;
    const timerId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % doctors.length);
    }, 2800);
    return () => window.clearInterval(timerId);
  }, [loading, doctors.length, hasUserInteracted, expandedDoctorId]);

  const totalDoctors = doctors.length;
  const activeDoctor = totalDoctors ? doctors[activeIndex] : null;
  const angleStep = totalDoctors ? 360 / totalDoctors : 0;

  const doctorItems = useMemo(() => {
    if (!totalDoctors) return [];
    return doctors.map((doctor, index) => {
      const rawOffset = index - activeIndex;
      const half = Math.floor(totalDoctors / 2);
      let offset = rawOffset;
      if (offset > half) offset -= totalDoctors;
      if (offset < -half) offset += totalDoctors;
      const angle = offset * angleStep;
      const radians = (angle * Math.PI) / 180;
      const depth = Math.cos(radians);
      const scale = 0.82 + (((depth + 1) / 2) * 0.18);
      const opacity = 0.26 + (((depth + 1) / 2) * 0.74);
      const lift = (1 - depth) * 18;
      return {
        doctor,
        index,
        offset,
        absOffset: Math.abs(offset),
        angle,
        depth,
        scale,
        opacity,
        lift
      };
    });
  }, [doctors, totalDoctors, activeIndex, angleStep]);

  function rotate(direction) {
    if (!totalDoctors) return;
    setHasUserInteracted(true);
    dismissSwipeHint();
    setActiveIndex((prev) => (prev + direction + totalDoctors) % totalDoctors);
  }

  function dismissSwipeHint() {
    if (!showSwipeHint) return;
    setShowSwipeHint(false);
  }

  function expandDoctor(doctor, index) {
    setActiveIndex(index);
    setExpandedDoctorId(doctor.id);
  }

  function handleCardClick(doctor, index, isActive) {
    setHasUserInteracted(true);
    dismissSwipeHint();
    if (isActive) {
      setExpandedDoctorId((prev) => (prev === doctor.id ? null : doctor.id));
      return;
    }
    setActiveIndex(index);
    setExpandedDoctorId(null);
  }

  function handlePointerStart(clientX) {
    setHasUserInteracted(true);
    dismissSwipeHint();
    pointerStartRef.current = clientX;
  }

  function handlePointerEnd(clientX) {
    if (pointerStartRef.current == null) return;
    const delta = clientX - pointerStartRef.current;
    pointerStartRef.current = null;
    if (Math.abs(delta) < 36) return;
    if (delta < 0) rotate(1);
    if (delta > 0) rotate(-1);
  }

  function getDoctorDescription(doctor) {
    const shortMessage = String(doctor.message || doctor.profile || '').trim();
    if (shortMessage) return shortMessage;
    const profileLines = Array.isArray(doctor.profileLong) ? doctor.profileLong.filter(Boolean) : [];
    if (profileLines.length) return profileLines[0];
    return doctor.message || doctor.profile || 'プロフィールは準備中です。';
  }

  function getDoctorExpandedSummary(doctor) {
    const lines = Array.isArray(doctor.profileLong) ? doctor.profileLong.filter(Boolean) : [];
    if (lines.length >= 2) return lines.slice(0, 2);
    if (lines.length === 1) return [lines[0]];
    return [doctor.message || doctor.profile || 'プロフィールは準備中です。'];
  }

  return (
    <>
      <Seo
        title="Drs. Farm ドクター"
        description="食と健康をつなぐ視点でDrs.Farmに参加する医師のプロフィールやメッセージを紹介します。"
        path="/doctors"
      />
      <h1>Drs. Farm ドクター</h1>
      <p className="page-lead">医師の提言と評価を通じて、食と健康のつながりをわかりやすく届けます。</p>

      <section className="panel reveal doctor-disk-panel" data-reveal>
        <p className="section-subcopy">円盤を回して、先生の紹介をご覧ください</p>
        <div className={`doctor-disk-shell ${expandedDoctorId ? 'has-expanded' : ''}`} aria-live="polite">
          <div className="doctor-disk-controls">
            <button type="button" className="btn btn-secondary doctor-nav-btn doctor-nav-btn-prev" onClick={() => rotate(-1)} disabled={!totalDoctors}>
              前へ
            </button>
            <button type="button" className="btn btn-secondary doctor-nav-btn doctor-nav-btn-next" onClick={() => rotate(1)} disabled={!totalDoctors}>
              次へ
            </button>
          </div>
          <div
            className={`doctor-disk-stage ${expandedDoctorId ? 'has-expanded' : ''}`}
            onTouchStart={(e) => handlePointerStart(e.touches[0].clientX)}
            onTouchEnd={(e) => handlePointerEnd(e.changedTouches[0].clientX)}
            onMouseDown={(e) => handlePointerStart(e.clientX)}
            onMouseUp={(e) => handlePointerEnd(e.clientX)}
            onMouseLeave={() => {
              pointerStartRef.current = null;
            }}
          >
            <div className="doctor-disk-base" aria-hidden="true" />
            {showSwipeHint && !expandedDoctorId ? (
              <button
                type="button"
                className="doctor-swipe-hint"
                onClick={dismissSwipeHint}
                aria-label="スワイプ案内を閉じる"
              >
                <span className="doctor-swipe-hint-text">右へスワイプして回転</span>
                <span className="doctor-swipe-hint-arrow" aria-hidden="true">→</span>
              </button>
            ) : null}
            <div className="doctor-disk-track" role="list" aria-label="ドクターメンバー">
              {doctorItems.map(({ doctor, index, angle, depth, scale, opacity, lift }) => {
                const isActive = index === activeIndex;
                const isExpanded = isActive && expandedDoctorId === doctor.id;
                const text = getDoctorDescription(doctor);
                const cardTransform = isExpanded
                  ? `translate3d(-50%, -50%, 0) translateY(${lift + 22}px) scale(1)`
                  : `translate3d(-50%, -50%, 0) rotateY(${angle}deg) translateZ(var(--doctor-ring-radius)) rotateY(${-angle}deg) translateY(${lift}px) scale(${scale})`;
                return (
                  <article
                    key={doctor.id}
                    className={`doctor-disk-card ${isActive ? 'is-active' : ''} ${isExpanded ? 'is-expanded' : ''}`}
                    style={{
                      transform: cardTransform,
                      opacity: isExpanded ? 1 : expandedDoctorId ? Math.min(opacity, 0.08) : opacity,
                      zIndex: isExpanded ? 220 : Math.round((depth + 1) * 100)
                    }}
                    onClick={() => handleCardClick(doctor, index, isActive)}
                    role="listitem"
                  >
                    <div className="doctor-disk-media">
                      {(doctor.profileImageUrl || doctor.photoUrl) ? (
                        <img
                          className={isExpanded ? 'doctor-disk-image is-expanded' : 'doctor-disk-image'}
                          src={doctor.profileImageUrl || doctor.photoUrl}
                          alt={doctor.name}
                          loading="lazy"
                          style={{ objectPosition: doctor.imagePosition || 'center center' }}
                        />
                      ) : null}
                    </div>
                    <div className="card-body">
                      <div className="doctor-card-head">
                        <h3>{doctor.name}</h3>
                        {isExpanded ? (
                          <button
                            type="button"
                            className="doctor-inline-close"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedDoctorId(null);
                            }}
                          >
                            CLOSE
                          </button>
                        ) : null}
                      </div>
                      {doctor.specialty ? <p className="meta-text">{doctor.specialty}</p> : null}
                      <p className={`doctor-description ${isExpanded ? '' : 'is-clamped'}`}>{text}</p>
                      {isExpanded ? (
                        <div className="doctor-inline-summary">
                          {getDoctorExpandedSummary(doctor).map((line, summaryIndex) => (
                            <p key={`${doctor.id}-summary-${summaryIndex}`}>{line}</p>
                          ))}
                        </div>
                      ) : null}
                      <div className="doctor-card-actions">
                        {!isExpanded ? (
                          <span className="meta-text doctor-card-hint">
                            {isActive ? 'クリックで紹介を見る' : 'クリックで中央に表示'}
                          </span>
                        ) : null}
                        {isExpanded ? (
                          <Link
                            className="text-btn"
                            to={`/doctors/${doctor.slug || doctor.id}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            詳細に進む
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
          {activeDoctor && !expandedDoctorId ? (
            <div className="doctor-disk-quick-link">
              <Link className="btn btn-primary" to={`/doctors/${activeDoctor.slug || activeDoctor.id}`}>
                {activeDoctor.name} 先生の詳細へ
              </Link>
            </div>
          ) : null}
        </div>

      </section>

      <section className="panel reveal" data-reveal>
        <h2>役割</h2>
        <p className="section-subcopy">第三者的立場での評価と発信</p>
        <p className="role-copy">
          DFドクターはサポーター会員の一部として、土壌分析などの結果に対して第三者の立場で評価を行います。
        </p>
      </section>
    </>
  );
}
